import React, { useEffect } from "react";
import Title from "antd/es/typography/Title";
import { Table, Card, Spin, Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBoxesStacked,
    faPalletBox,
    faStopwatch,
    faTruck,
} from "@fortawesome/pro-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import WarehouseService from "../services/Warehouses/WarehouseService";

import { getVesselColourClasses } from "../helpers/common";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import dayjs from "../helpers/dayjsSetup";
import { addDays, format } from "date-fns";
import WarehouseSelect from "../components/WarehouseSelect";
import { useGlobalFilters } from "../providers/GlobalFiltersProvider";
import WarehouseFilter from "../components/Filters/WarehouseFilter";
import { useContainerDeliveryMethods } from "../customHooks/useContainerDeliveryMethods";
import { Container } from "lucide-react";

const getISODate = (date) => (date ? format(date, "yyyy-MM-dd") : null);

const WarehouseUnloadingPlan = ({ warehouses }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [showWarehouseColumn, setShowWarehouseColumn] = useState(false);
    // const [warehouseId, setWarehouseId] = useState(null);
    const { data: containerDeliveryMethods } = useContainerDeliveryMethods();
    const { filters } = useGlobalFilters();
    const deliveryDateFromState = location.state?.deliveryDate;

    const { data } = useQuery({
        queryKey: ["nextDeliveryDate", filters],
        queryFn: async () =>
            WarehouseService.getWarehouseShipmentsNextDate({
                deliveryDate: getISODate(new Date()),
                ...filters,
            }),
        enabled: !deliveryDateFromState, // only fetch if no state value
    });

    useEffect(() => {
        if (deliveryDateFromState) {
            setDeliveryDate(new Date(deliveryDateFromState));
        } else if (data) {
            setDeliveryDate(new Date(data));
        } else if (!deliveryDateFromState && data === null) {
            setDeliveryDate(new Date());
        }
    }, [deliveryDateFromState, data]);


    const { data: warehouseShipments, isLoading } = useQuery({
        queryKey: ["warehouseShipments", deliveryDate, filters],
        queryFn: async () =>
            await WarehouseService.getWarehouseShipments({deliveryDate: getISODate(deliveryDate), ...filters}),
    });

    // Extract unique time slots from dummy JSON
    const extractTimeSlots = (data) => {
        const times = new Set();

        data?.forEach((item) => {
            item.containers.forEach((container) => {
                if (container.deliveryDate) {
                    const etaTime = new Date(container.deliveryDate).toLocaleTimeString(
                        [],
                        {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                        }
                    );
                    times.add(etaTime);
                }
            });
        });

        // Sort times in ascending order
        return Array.from(times).sort((a, b) => {
            const [hourA, minA] = a.split(":").map(Number);
            const [hourB, minB] = b.split(":").map(Number);
            return hourA - hourB || minA - minB;
        });
    };

    const timeSlots = extractTimeSlots(warehouseShipments); // Example: ["07:00", "08:00", "09:00", ...]

    const onContainerClick = (containerID) => {
        navigate("/shipment-gateway/shipment-details", {
            state: {
                pathname: location.pathname,
                containerID,
                deliveryDate,
                // warehouseId,
            },
        });
    };
    // Prepare table data
    const tableData = warehouseShipments?.map((item, index) => {
        const row = {
            key: index,
            warehouseName: item.warehouseName || "",
            receivingZoneName: item.receivingZoneName || "",
            dockCode: item.dockCode || "",
            containersBySlot: {},
        };

        timeSlots.forEach((slot) => {
            const containersAtSlot = item.containers.filter((c) => {
                if (!c.deliveryDate) return false;
                const etaTime = new Date(c.deliveryDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
                return etaTime === slot;
            });
            row.containersBySlot[slot] = containersAtSlot;
        });

        return row;
    });

    useEffect(() => {
        const uniqueWarehouses = new Set(tableData?.map((item) => item.warehouseName));
        setShowWarehouseColumn(uniqueWarehouses.size > 2);
    }, [tableData]);

    // Calculate footer totals dynamically
    const totals = {
        boxes: Array(timeSlots.length).fill(0),
        pallets: Array(timeSlots.length).fill(0),
        manHours: Array(timeSlots.length).fill(0),
    };

    warehouseShipments?.forEach((item) => {
        timeSlots.forEach((slot, idx) => {
            const containersAtSlot = item.containers.filter((c) => {
                if (!c.deliveryDate) return false;
                const etaTime = new Date(c.deliveryDate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                });
                return etaTime === slot;
            });

            containersAtSlot.forEach((c) => {
                totals.boxes[idx] += c.boxes || 0;
                totals.pallets[idx] += c.pallets || 0;
                totals.manHours[idx] +=
                    (c.boxesUnloadManHours || 0) + (c.palletsUnloadManHours || 0);
            });
        });
    });

    // Columns with grouped headers
    const columns = [
        {
            title: "Warehouse",
            dataIndex: "warehouseName",
            key: "warehouseName",
            width: 180,
            hidden: showWarehouseColumn ? false : true,
        },
        {
            title: "Receiving Zone",
            dataIndex: "receivingZoneName",
            key: "receivingZoneName",
            width: 150,
        },
        {
            title: "Dock Code",
            dataIndex: "dockCode",
            key: "dockCode",
            width: 120,
        },
        {
            title: "Arrival Time",
            children: timeSlots?.map((slot) => ({
                title: slot,
                key: slot,
                render: (_, record) => {
                    const containers = record.containersBySlot[slot];
                    return containers && containers.length ? (
                        <div className="flex flex-wrap flex-col">
                            {containers?.map((container) => {
                                const vesselClasses = getVesselColourClasses(container.vessel);
                                return (
                                    <div
                                        key={container.containerID}
                                        className={`${vesselClasses.bg} p-1 rounded-lg m-1 w-[200px]`}
                                        onClick={() => onContainerClick(container.containerID)}
                                    >
                                        <div className="bg-white ml-2 px-1 hover:bg-gray-100 cursor-pointer rounded">
                                            <div className="flex justify-between">
                                                <span className="font-semibold text-md">
                                                    {container.containerNumber}
                                                </span>
                                                <span className="text-xs">
                                                    {container.containerLoadType === "LCL"
                                                        ? container.containerLoadType
                                                        : container.containerType}
                                                </span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                        <span>
                                                            {container.deliveryMethodID != null &&
                                                                containerDeliveryMethods?.find(
                                                                    (method) => method.id === container.deliveryMethodID
                                                                )?.name}
                                                        </span>
                                                        {container.isOnSite && (
                                                            <span className="text-green-500">
                                                                On Site
                                                            </span>
                                                        )}
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span className="text-xs">
                                                    {container.boxes !== null &&
                                                        container.boxes !== undefined &&
                                                        container.boxes !== 0 && (
                                                            <span className="mr-[4px] text-[9px] text-[#2a1313]">
                                                                <FontAwesomeIcon icon={faBoxesStacked} />
                                                                <span className="text-[12px] text-[#545454] font-medium font-inter">
                                                                    &nbsp;{container.boxes}
                                                                </span>
                                                            </span>
                                                        )}
                                                    {container.pallets !== null &&
                                                        container.pallets !== undefined &&
                                                        container.pallets !== 0 && (
                                                            <span className="mr-[4px] text-[9px] text-[#545454]">
                                                                <FontAwesomeIcon icon={faPalletBox} />
                                                                <span className="text-[12px] text-[#545454] font-medium font-inter">
                                                                    &nbsp;{container.pallets}
                                                                </span>
                                                            </span>
                                                        )}
                                                    {((container.boxesUnloadManHours !== null &&
                                                        container.boxesUnloadManHours !== undefined &&
                                                        container.boxesUnloadManHours !== 0) ||
                                                        (container.palletsUnloadManHours !== null &&
                                                            container.palletsUnloadManHours !== undefined &&
                                                            container.palletsUnloadManHours !== 0)) && (
                                                            <span className="text-[9px] text-[#545454]">
                                                                <FontAwesomeIcon icon={faStopwatch} />
                                                                <span className="text-[12px] text-[#545454] font-medium font-inter">
                                                                    &nbsp;
                                                                    {container.boxesUnloadManHours +
                                                                        container.palletsUnloadManHours}
                                                                </span>
                                                            </span>
                                                        )}
                                                </span>
                                                {container.deliveryDate && (
                                                    <FontAwesomeIcon
                                                        icon={faTruck}
                                                        className={`text-xs ${container.isDeliveryConfirmed
                                                            ? "text-green-500"
                                                            : "text-red-500"
                                                            }`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        ""
                    );
                },
            })),
        },
    ];

    return (
    <div className="px-2 relative  w-full h-full flex flex-col gap-2">
            {/* <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1 mx-1 pl-2 space-y-1 md:space-y-0"> */}
                <Spin spinning={isLoading} size="large">
                    <div className="flex flex-col md:flex-row justify-between items-center w-full my-2 px-2">
                        <div className="flex justify-start items-center font-inter">                    <span
                            className="!font-medium !text-[#015373] text-[30px] !m-0 pl-4 mt-10"
                        >
                            Unloading Plan
                        </span>
                        </div>
                        <div className="flex justify-between font-inter">
                            <WarehouseFilter warehouses={warehouses} />
                        </div>
                    </div>
                    <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
                        <div className="mb-4">
                            <CustomDateTimePicker
                                type="date"
                                value={deliveryDate ? dayjs(deliveryDate) : null}
                                onChange={(d) => setDeliveryDate(d ? d.toDate() : null)}
                                className="h-10 rounded-lg"
                                placeholder="Select Delivery Date"
                            />
                            <Button
                                className="h-10 bg-[#F7F6F8] font-bold ml-2 align-middle"
                                onClick={() =>
                                    setDeliveryDate(addDays(deliveryDate ?? new Date(), -1))
                                }
                            >
                                <LeftOutlined />
                            </Button>
                            <Button
                                className="h-10 bg-[#F7F6F8] font-bold ml-2 align-middle mr-4"
                                onClick={() =>
                                    setDeliveryDate(addDays(deliveryDate ?? new Date(), 1))
                                }
                            >
                                <RightOutlined />
                            </Button>

                            {/* <WarehouseSelect placeholder="Select Warehouse" value={warehouseId} onChange={setWarehouseId} allowClear /> */}
                        </div>

                        <Table
                            size="small"
                            columns={columns}
                            dataSource={tableData}
                            pagination={false}
                            bordered
                            scroll={{ x: "max-content", y: 700 }}
                            rowClassName={(record) => record.warehouseName=="" ? "bg-[#E9E9E9] bg-opacity-70" : ""}
                            summary={() => (
                                <>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                                            <strong>Boxes</strong>
                                        </Table.Summary.Cell>
                                        {totals.boxes.map((val, idx) => (
                                            <Table.Summary.Cell key={`boxes-${idx}`}>
                                                {val || ""}
                                            </Table.Summary.Cell>
                                        ))}
                                    </Table.Summary.Row>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                                            <strong>Pallets</strong>
                                        </Table.Summary.Cell>
                                        {totals.pallets.map((val, idx) => (
                                            <Table.Summary.Cell key={`pallets-${idx}`}>
                                                {val || ""}
                                            </Table.Summary.Cell>
                                        ))}
                                    </Table.Summary.Row>
                                    <Table.Summary.Row>
                                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                                            <strong>Man Hours</strong>
                                        </Table.Summary.Cell>
                                        {totals.manHours.map((val, idx) => (
                                            <Table.Summary.Cell key={`hours-${idx}`}>
                                                {val || ""}
                                            </Table.Summary.Cell>
                                        ))}
                                    </Table.Summary.Row>
                                </>
                            )}
                        />
                    </Card>
                </Spin>
            {/* </div> */}
        </div>
    );
};

export default WarehouseUnloadingPlan;